<template>
    <InsightCardLayout>
        <template v-if="interactive && showRating" #before>
            <MyWorkInsightFeedback
                :feedback-value="feedbackValue"
                :feedback-tags="feedbackTags"
                :feedback-comment="feedbackComment"
                :disabled="feedbackFetching"
                :feedback-submitting="feedbackSubmitting"
                :submit-error="!!submitError"
                @select="handleFeedbackSelect"
                @submit-feedback="handleSubmitFeedback" />
        </template>
        <!-- Category + date row -->
        <div class="flex items-center gap-1.5 mb-3">
            <InsightCategoryLabel v-if="experimentTitle" :label="experimentTitle" :tier="tier" />
            <span v-if="createdAt" class="scaffold-details-small text-insight-eyebrow-text">• {{ DateTime.fromISO(createdAt).toFormat("MMM d") }}</span>
        </div>

        <!-- Title -->
        <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
        <h3 class="display-small-serif text-valence-grey-850 mb-3">
            <MarkdownText :text="title" inline />
        </h3>

        <!-- Divider -->
        <div class="border-t border-valence-grey-250 mb-2.5" role="separator"></div>

        <!-- Summary with inline "See more" -->
        <div
            class="summary-with-toggle body-small prose prose-sm prose-slate max-w-none text-valence-grey-850 leading-relaxed prose-headings:my-0.5 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-li:leading-5">
            <MarkdownText :text="summary" />
            <button
                v-if="hasExpandableContent && showSeeMore"
                type="button"
                :aria-expanded="isExpanded"
                :aria-controls="`insight-detail-${insightId}`"
                class="see-more-toggle text-valence-primary-500 hover:text-valence-primary-700 transition-colors focus-visible:ring-2 focus-visible:ring-valence-primary-500 rounded"
                @click="isExpanded = !isExpanded">
                {{ isExpanded ? "See less" : "See more" }}
            </button>
        </div>

        <!-- Expandable detail box -->
        <div v-if="isExpanded" :id="`insight-detail-${insightId}`" class="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-2.5 my-2.5 flex flex-col gap-3">
            <div v-if="content" class="detail-with-label prose prose-sm prose-slate max-w-none text-valence-grey-850 leading-relaxed prose-p:my-1 prose-ul:my-1 prose-ol:my-1">
                <span class="detail-label font-bold">Why I'm flagging this: </span>
                <MarkdownText :text="content" />
            </div>
        </div>

        <!-- Unknowns -->
        <div v-if="unknowns.length" class="flex flex-col gap-1.5 mt-2.5">
            <span class="body-small-bold text-valence-grey-850">What I'd love to understand:</span>
            <ul class="list-disc pl-5">
                <li v-for="unknown in unknowns" :key="unknown" class="body-small italic text-valence-grey-850 leading-relaxed">
                    {{ unknown }}
                </li>
            </ul>
        </div>

        <!-- Footer: divider + Based on chips + Dive Deeper -->
        <div class="border-t border-valence-grey-250 mt-3 pt-3 flex items-center justify-between gap-4">
            <InsightEvidenceChips :evidence-groups="evidenceGroups" :legacy-sources="sourcesUsed" :analytics-context="{ insightId, experimentSlug, location }" />
            <DiveDeeperButton v-if="interactive" :loading="isStartingChat" @click="handleDiveDeeper" />
        </div>
        <span v-if="chatError" role="alert" class="details text-rose-600 mt-1">{{ chatError }}</span>
    </InsightCardLayout>
</template>

<script setup lang="ts">
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import { type LabInsightLocation, useLabInsightActions } from "~vue/composables/useLabInsightActions";
import DiveDeeperButton from "~vue/Labs/DiveDeeperButton.vue";
import { evidenceSourceCounts, type InsightEvidenceGroup } from "~vue/Labs/evidence";
import { getInsightSourcesUsed, tierForExperimentSlug } from "~vue/Labs/insightCategory";
import InsightCategoryLabel from "~vue/Labs/InsightCategoryLabel.vue";
import InsightEvidenceChips from "~vue/Labs/InsightEvidenceChips.vue";
import MyWorkInsightFeedback from "~vue/Labs/MyWorkInsightFeedback.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { computed, onMounted, ref } from "vue";

interface Props {
    experimentTitle: string;
    experimentSlug: string;
    title: string;
    summary: string;
    content?: string;
    insightId: string;
    unknowns?: string[];
    showSeeMore?: boolean;
    createdAt?: string | null;
    perspectiveEnabled?: boolean;
    collaborationProfileEnabled?: boolean;
    workIqEnabled?: boolean;
    perspectivePresent?: boolean;
    collaborationProfilePresent?: boolean;
    workIqPresent?: boolean;
    calendarEnabled?: boolean;
    evidenceGroups?: InsightEvidenceGroup[];
    /** Where the card is rendered, for analytics and evidence-chip context. */
    location?: LabInsightLocation;
    /**
     * Disable actions during onboarding to prevent leaving setup for chat and requesting ratings on the
     * user's first insight.
     */
    interactive?: boolean;
    /**
     * Separate from interactive so setup's final three cards can offer Dive Deeper without displaying three
     * rating prompts.
     */
    showRating?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    content: "",
    unknowns: () => [],
    showSeeMore: false,
    createdAt: null,
    perspectiveEnabled: false,
    collaborationProfileEnabled: false,
    workIqEnabled: false,
    perspectivePresent: false,
    collaborationProfilePresent: false,
    workIqPresent: false,
    calendarEnabled: true,
    location: "mywork",
    interactive: true,
    showRating: true,
});

const isExpanded = ref(false);
const hasExpandableContent = computed(() => Boolean(props.content));
const tier = computed(() => tierForExperimentSlug(props.experimentSlug));

const sourcesUsed = computed(() => getInsightSourcesUsed(tier.value, props));

const cardEventData = () => ({
    insight_id: props.insightId,
    experiment_title: props.experimentTitle,
    experiment_slug: props.experimentSlug,
    location: props.location,
    evidence_source_counts: evidenceSourceCounts(props.evidenceGroups ?? []),
});

const {
    feedbackValue,
    feedbackTags,
    feedbackComment,
    feedbackFetching,
    feedbackSubmitting,
    submitError,
    handleFeedbackSelect,
    handleSubmitFeedback,
    isStartingChat,
    chatError,
    handleStartChat,
} = useLabInsightActions(props.insightId, props.location, { interactive: props.interactive });

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_SHOWN, cardEventData());
});

function handleDiveDeeper() {
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_CLICKED, cardEventData());
    handleStartChat();
}
</script>

<style scoped>
/* Make the last paragraph from MarkdownText render inline so the
   "See more" button flows on the same line as the summary text.
   Chain: wrapper → MarkdownText root (inline) → [data-marked] div (inline) → last <p> (inline) → button (inline) */
.summary-with-toggle :deep([data-marked]) {
    display: inline;
}

.summary-with-toggle :deep([data-marked] > p:last-of-type) {
    display: inline;
}

.summary-with-toggle .see-more-toggle {
    display: inline;
}

/* Make the bold label flow inline with the first paragraph of the detail content. */
.detail-with-label .detail-label {
    display: inline;
}

.detail-with-label :deep([data-marked]) {
    display: inline;
}

.detail-with-label :deep([data-marked] > p:first-of-type) {
    display: inline;
}
</style>
